// TODO: relocate client specific language; see https://hopper-jira.atlassian.net/wiki/spaces/ENGWEB/pages/1959952414/Discussion+on+practical+ways+to+prevent+adding+Cap+One+specific+language+into+Pawtucket
import { cardsToShowLastFourOnRewardsBanner } from "redmond";

export const EXPERIENCES_REWARDS_DISABLED_BANNER_TEXT =
  "Rewards redemptions are temporarily unavailable. Card purchases will still earn rewards normally.";

// Common interface for the banner text function parameters
interface RewardsBannerTextProps {
  firstName: string;
  carsEarnRate: string | number;
  experiencesEarnRate: string | number;
  flightEarnRate: string | number;
  hotelsEarnRate: string | number;
  homesEarnRate: string | number;
  productId: string;
  lastFour: string;
  currency: string;
}

// Product type definitions for generating reward messages
interface ProductType {
  id: string;
  earnRateKey: keyof RewardsBannerTextProps;
  displayName: string;
  displayNamePlural: string;
}

// Priority order of product type in the banner message, grouped by earn rate
const PRODUCT_TYPES: ProductType[] = [
  {
    id: "flights",
    earnRateKey: "flightEarnRate",
    displayName: "flight",
    displayNamePlural: "flights",
  },
  {
    id: "homes",
    earnRateKey: "homesEarnRate",
    displayName: "vacation rental",
    displayNamePlural: "vacation rentals",
  },
  {
    id: "experiences",
    earnRateKey: "experiencesEarnRate",
    displayName: "activity",
    displayNamePlural: "activities",
  },
  {
    id: "hotels",
    earnRateKey: "hotelsEarnRate",
    displayName: "hotel",
    displayNamePlural: "hotels",
  },
  {
    id: "cars",
    earnRateKey: "carsEarnRate",
    displayName: "rental car",
    displayNamePlural: "rental cars",
  },
];

/**
 * Generates a reward rate string for a specific product type
 */
const generateRateString = (
  earnRate: string | number,
  multiplier: string,
  currency: string
): string => `${earnRate}${multiplier} ${currency}`;

/**
 * Creates a reward banner text based on the different earn rates
 */
const createRewardsBannerText = (props: RewardsBannerTextProps): string => {
  const { firstName, productId, lastFour, currency } = props;

  const multiplier = currency.includes("cash") ? "%" : "X";

  // Start with greeting
  let earnString = `Hi ${firstName}, earn `;

  // Group products by their earn rates
  const productsByRate: { [rate: string]: ProductType[] } = {};

  // Filter out products without earn rates
  const availableProducts = PRODUCT_TYPES.filter((product) => {
    const earnRate = props[product.earnRateKey];
    return earnRate !== undefined && earnRate !== null && earnRate !== "";
  });

  // Group products by their earn rates
  availableProducts.forEach((product) => {
    const earnRate = props[product.earnRateKey] as string | number;
    const rateKey = String(earnRate);

    if (!productsByRate[rateKey]) {
      productsByRate[rateKey] = [];
    }

    productsByRate[rateKey].push(product);
  });

  // Generate rate strings for each group
  const rateGroups = Object.entries(productsByRate).map(([rate, products]) => {
    const rateString = generateRateString(rate, multiplier, currency);

    // Format product names
    if (products.length === 1) {
      return `${rateString} on ${products[0].displayNamePlural}`;
    }

    // For multiple products with the same rate
    const lastProduct = products[products.length - 1];
    const otherProducts = products.slice(0, products.length - 1);

    const productList = otherProducts
      .map((p) => p.displayNamePlural)
      .join(", ");

    return `${rateString} on ${productList}, & ${lastProduct.displayNamePlural}`;
  });

  // Connect all rate groups into a single string
  if (rateGroups.length === 1) {
    // Only one rate group (all products have the same rate)
    earnString += `${rateGroups[0]}`;
  } else {
    // Multiple rate groups
    const lastGroup = rateGroups[rateGroups.length - 1];
    const otherGroups = rateGroups.slice(0, rateGroups.length - 1);

    earnString += `${otherGroups.join(", ")}, and ${lastGroup}`;
  }

  // Add account info
  earnString += ` with ${productId}`;

  // Add card number if applicable
  if (cardsToShowLastFourOnRewardsBanner.includes(productId.toLowerCase())) {
    earnString += ` ending in ${lastFour}`;
  } else {
    earnString += `.`;
  }

  return earnString;
};

export const HOME_PAGE_BANNER_TEXT = (props: RewardsBannerTextProps): string =>
  createRewardsBannerText(props);
